import { initializeStack } from './thin-film-stack.js';
import { MATERIALS, DEFAULTS, QUOTE_KEY, findMaterial, calculate, convert, format, materialName, resultText, resultsCsv, encode, decode } from './thin-film-core.js';

export function initializeThinFilm(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = 'true';
  const field = key => root.querySelector(`[data-field="${key}"]`);
  const node = key => root.querySelector(`[data-${key}]`);
  let state = { ...DEFAULTS };
  let result = null;
  let currentMaterial = state.material;
  const mapping = JSON.parse(node('material-links').textContent);
  const status = message => { node('status').textContent = message; };
  function read() {
    const next = { ...state };
    for (const key of Object.keys(DEFAULTS)) {
      const input = field(key);
      if (input) next[key] = input.type === 'checkbox' ? input.checked : input.value;
    }
    const selection = field('material').value.trim();
    next.material = /^custom material$/i.test(selection) ? 'custom' : findMaterial(selection)?.symbol || '';
    next.sourceEnabled = node('source-details').open;
    return next;
  }
  function load(next) {
    state = { ...next };
    currentMaterial = next.material;
    for (const key of Object.keys(DEFAULTS)) {
      const input = field(key);
      if (!input) continue;
      if (input.type === 'checkbox') input.checked = next[key];
      else input.value = key === 'material' ? (next.material === 'custom' ? 'Custom Material' : `${next.material} — ${MATERIALS.find(m => m.symbol === next.material).name}`) : next[key];
    }
    node('source-details').open = next.sourceEnabled;
    update();
  }
  function update() {
    state = read();
    const custom = state.material === 'custom';
    node('custom-fields').hidden = !custom;
    node('rectangle').hidden = state.geometry !== 'rectangle';
    node('circle').hidden = state.geometry !== 'circle';
    node('area').hidden = state.geometry !== 'area';
    node('dimension-unit').hidden = state.geometry === 'area';
    node('sides-wrap').hidden = state.geometry === 'area';
    node('custom-links').hidden = !custom;
    node('alloy-link').hidden = !custom || !state.customAlloy;
    node('reset-density').hidden = custom || !state.material;
    const reference = MATERIALS.find(m => m.symbol === state.material);
    node('density-status').textContent = reference
      ? `NIST reference: ${reference.density} g/cm³. ${state.densityOverridden || Number(state.density) !== reference.density ? 'Using your density override.' : 'Using the reference density.'}`
      : 'Enter a density appropriate to your material and process. Alloy density is not inferred from composition.';
    for (const kind of ['evaporation', 'targets']) node(`${kind}-link`).href = mapping[state.material]?.[kind] || node(`${kind}-link`).dataset.fallback;
    node('mapping-note').textContent = mapping[state.material]
      ? 'Element links use the existing catalog filters and may include compounds or alloys containing this element. Confirm the material and form on the product page.'
      : 'Browse the full categories; no verified element-specific match is being assumed.';
    root.querySelectorAll('[aria-invalid="true"]').forEach(input => input.removeAttribute('aria-invalid'));
    node('error').textContent = '';
    result = null;
    node('result').hidden = true;
    node('empty').hidden = false;
    node('source-result').hidden = true;
    node('share-wrap').hidden = true;
    node('copy-wrap').hidden = true;
    try {
      result = calculate(state);
      state = result.input;
      node('result').hidden = false;
      node('empty').hidden = true;
      node('total-area').textContent = `${format(result.totalAreaCm2)} cm²`;
      node('volume').textContent = `${format(result.volumeCm3)} cm³`;
      node('mass').textContent = `${format(result.massG * 1000)} mg`;
      node('mass-grams').textContent = `${format(result.massG)} g`;
      node('result-assumptions').textContent = `${materialName(state)} · Density ${state.density} g/cm³ (${state.densityOverridden ? 'user-provided / overridden' : 'NIST reference'}). Uniform film thickness. ${state.geometry === 'area' ? 'Entered area already includes all coated faces per substrate; no side multiplier.' : `${state.count} substrate(s), ${state.sides} coated side(s) each; identical area and thickness on both sides when selected.`}`;
      if (state.sourceEnabled) {
        node('source-result').hidden = false;
        node('source-mass').textContent = `${format(result.sourceG * 1000)} mg (${format(result.sourceG)} g)`;
        node('planning-mass').textContent = `${format(result.planningG * 1000)} mg (${format(result.planningG)} g)`;
      }
    } catch (error) {
      node('error').textContent = error.message;
      field(error.field)?.setAttribute('aria-invalid', 'true');
    }
    root.querySelectorAll('[data-action]').forEach(button => { button.disabled = !result; });
  }
  root.addEventListener('input', event => {
    if (event.target === field('density')) state.densityOverridden = true;
    if (event.target === field('material')) {
      const selection = /^custom material$/i.test(event.target.value.trim()) ? 'custom' : findMaterial(event.target.value)?.symbol;
      if (selection && selection !== currentMaterial) {
        currentMaterial = selection;
        field('density').value = selection === 'custom' ? '' : MATERIALS.find(m => m.symbol === selection).density;
        state.densityOverridden = selection === 'custom';
      }
    }
    update();
  });
  root.addEventListener('change', event => {
    const key = event.target.dataset.field;
    const kind = { lengthUnit: 'length', areaUnit: 'area', thicknessUnit: 'thickness' }[key];
    if (kind && event.target.value !== state[key]) {
      const keys = kind === 'length' ? ['length', 'width', 'diameter'] : kind === 'area' ? ['area'] : ['thickness'];
      for (const k of keys) field(k).value = convert(field(k).value, kind, state[key], event.target.value);
    }
    update();
  });
  // Select input events arrive before change. Handle unit conversion before reading new units.
  root.addEventListener('input', event => {
    const key = event.target.dataset.field;
    const kind = { lengthUnit: 'length', areaUnit: 'area', thicknessUnit: 'thickness' }[key];
    if (!kind || event.target.value === state[key]) return;
    for (const k of (kind === 'length' ? ['length', 'width', 'diameter'] : kind === 'area' ? ['area'] : ['thickness'])) field(k).value = convert(field(k).value, kind, state[key], event.target.value);
  }, true);
  node('source-details').addEventListener('toggle', update);
  node('reset-density').addEventListener('click', () => {
    const material = MATERIALS.find(m => m.symbol === read().material);
    if (!material) return;
    field('density').value = material.density;
    state.densityOverridden = false;
    update();
  });
  function download(content) {
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'thin-film-calculation.csv'; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  root.addEventListener('click', async event => {
    const action = event.target.closest('[data-action]')?.dataset.action;
    if (!action || !result) return;
    try {
      if (action === 'csv') { download(resultsCsv(result)); status('CSV downloaded with parameters, results, units and assumptions.'); }
      if (action === 'copy') {
        const text = resultText(result);
        try { await navigator.clipboard.writeText(text); status('Results copied, including units and assumptions.'); }
        catch { node('copy-wrap').hidden = false; node('copy-text').value = text; node('copy-text').focus(); node('copy-text').select(); status('Clipboard is unavailable. Select and copy the results below.'); }
      }
      if (action === 'share') {
        const url = new URL(node('base-url').href); url.search = ''; url.hash = `tf=${encode(result.input)}`;
        node('share-wrap').hidden = false; node('share-url').value = url.href;
        try { await navigator.clipboard.writeText(url.href); status('Share link copied. It contains your calculation inputs; do not share confidential parameters.'); }
        catch { status('Copy the share link below. It contains your calculation inputs; do not share confidential parameters.'); }
      }
      if (action === 'quote') {
        const payload = { schema: 'basis-thin-film-quote', version: 1, material: materialName(result.input), summary: resultText(result) };
        try { sessionStorage.setItem(QUOTE_KEY, JSON.stringify(payload)); }
        catch { throw new Error('Browser session storage is unavailable. Copy Results and paste them into your inquiry.'); }
        location.assign(node('quote-url').href);
      }
    } catch (error) { status(error.message); }
  });
  load(DEFAULTS);
  function restore() {
    const encoded = new URLSearchParams(location.hash.slice(1)).get('tf');
    if (!encoded) return;
    try { load(decode(encoded)); status('Calculation restored from the share link. Review all inputs before use.'); }
    catch (error) { status(error.message); }
  }
  restore();
  initializeStack(root);
  window.addEventListener('hashchange', restore);
}
document.querySelectorAll('[data-thin-film-calculator]').forEach(initializeThinFilm);
document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-thin-film-calculator]').forEach(initializeThinFilm));
